import { useCallback, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import GradientDivider from './GradientDivider';
import { AIDocumentClassification, ReferencedIn } from './SuggestedDocumentReference.props';
import {
  commentaryItemSx,
  commentaryTitleSx,
  compactContentSx,
  compactIconSx,
  compactPaperSx,
  documentColorSx,
  expandedHeaderSx,
  expandedIconSx,
  expandedPaperSx,
  expandedTitleSx,
  referenceTypeColorSx,
  suggestionLinkSx,
  whySuggestionTextSx,
} from './SuggestedDocumentReference.styles';
import SuggestionActionButtons from './SuggestionActionButtons';

const CLASSIFICATION_DISPLAY_MAP: Record<string, string> = {
  CAP_TABLE: 'Cap Table',
  BALANCE_SHEET: 'Balance Sheet',
  INCOME_STATEMENT: 'Income Statement',
  CASH_FLOW_STATEMENT: 'Cash Flow Statement',
  OTHER: 'Other',
};

const HOVER_CLOSE_DELAY = 50;

export interface AISuggestionPopoverProps {
  documentReferenceId: number | string;
  aiClassification?: AIDocumentClassification | null;
  /** Plain string commentary – used when aiClassification is not available */
  commentary?: string[];
  referencedIn?: ReferencedIn[];
  /** When true, the popover opens directly in expanded mode */
  defaultExpanded?: boolean;
  /** Custom expanded header title */
  title?: string;
  /** Custom expanded header subtitle (ReactNode for rich content) */
  subtitle?: React.ReactNode;
  /** Custom content rendered between header and action buttons in expanded view */
  expandedContent?: React.ReactNode;
  /** Label for the accept button in expanded view */
  acceptLabel?: string;
  /** 'hover' opens on mouse enter, 'click' opens on click */
  trigger?: 'hover' | 'click';
  onAccept: () => void;
  onDecline: () => void;
  children: (props: {
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave: () => void;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    ariaDescribedBy: string | undefined;
  }) => React.ReactNode;
}

const AISuggestionPopover = ({
  documentReferenceId,
  aiClassification,
  commentary: commentaryStrings,
  referencedIn,
  defaultExpanded = false,
  title,
  subtitle,
  expandedContent,
  acceptLabel = 'Accept this Suggestion',
  onAccept,
  onDecline,
  children,
}: AISuggestionPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback((event: React.MouseEvent<HTMLElement>) => {
    clearCloseTimeout();
    if (!expandedRef.current) {
      if (defaultExpanded) {
        expandedRef.current = true;
        setExpanded(true);
      }
      setAnchorEl(event.currentTarget);
    }
  }, [clearCloseTimeout, defaultExpanded]);

  const handleMouseLeave = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setAnchorEl(null);
      if (expandedRef.current) {
        expandedRef.current = false;
        setExpanded(false);
      }
    }, HOVER_CLOSE_DELAY);
  }, []);

  const handlePopperMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handlePopperMouseLeave = useCallback(() => {
    closeTimeout.current = setTimeout(() => {
      setAnchorEl(null);
      if (expandedRef.current) {
        expandedRef.current = false;
        setExpanded(false);
      }
    }, HOVER_CLOSE_DELAY);
  }, []);

  const handleClose = useCallback(() => {
    clearCloseTimeout();
    setAnchorEl(null);
    expandedRef.current = false;
    setExpanded(false);
  }, [clearCloseTimeout]);

  const handleExpand = useCallback(() => {
    clearCloseTimeout();
    expandedRef.current = true;
    setExpanded(true);
  }, [clearCloseTimeout]);

  const handleAccept = useCallback(() => {
    handleClose();
    onAccept();
  }, [handleClose, onAccept]);

  const handleDecline = useCallback(() => {
    handleClose();
    onDecline();
  }, [handleClose, onDecline]);

  const open = Boolean(anchorEl);
  const popperId = open ? `suggestion-popper-${documentReferenceId}` : undefined;

  const classification = aiClassification;
  const classificationResult = classification?.classification_result || 'Document';
  const displayName = CLASSIFICATION_DISPLAY_MAP[classificationResult] || classificationResult;

  const suggestedReference = referencedIn?.[0];
  const referenceType = suggestedReference?.reference_object_type || displayName;

  // Normalize commentary from either source
  const normalizedCommentary: { content: string }[] = classification?.commentary
    ?? commentaryStrings?.map(s => ({ content: s }))
    ?? [];

  return (
    <>
      {children({
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onClick: () => {},
        ariaDescribedBy: popperId,
      })}

      {/* Hover / Expanded popover */}
      <Popper
        id={popperId}
        open={open}
        anchorEl={anchorEl}
        placement="top"
        modifiers={[
          { name: 'offset', options: { offset: [0, 4] } },
          { name: 'flip', options: { fallbackPlacements: ['bottom-start', 'top-end'] } },
        ]}
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          {expanded ? (
            <Paper
              onMouseEnter={handlePopperMouseEnter}
              onMouseLeave={handlePopperMouseLeave}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              elevation={4}
              sx={expandedPaperSx}
            >
              {/* Header */}
              <Box sx={expandedHeaderSx}>
                <Box component="img" src="/images/scalar_ai.png" alt="Scalar AI" sx={expandedIconSx} />
                <Box>
                  <Typography sx={expandedTitleSx}>
                    {title ?? 'Suggested Reference'}
                  </Typography>
                  {subtitle ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {subtitle}
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Add this{' '}
                      <Typography component="span" variant="body2" sx={documentColorSx}>
                        Document
                      </Typography>{' '}
                      to{' '}
                      <Typography component="span" variant="body2" sx={referenceTypeColorSx}>
                        {referenceType}
                      </Typography>
                    </Typography>
                  )}
                </Box>
              </Box>

              <GradientDivider />

              {expandedContent ?? (
                <Box>
                  <Typography variant="body2" sx={commentaryTitleSx}>
                    Why this suggestion?
                  </Typography>
                  {normalizedCommentary.length > 0 && (
                    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                      {normalizedCommentary.map((note, index) => (
                        <Typography
                          key={index}
                          component="li"
                          variant="body2"
                          color="text.secondary"
                          sx={commentaryItemSx}
                        >
                          {note.content}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Confidence indicator */}
              {classification?.confidence != null && (
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">
                    Confidence: {Math.round(classification.confidence * 100)}%
                  </Typography>
                </Box>
              )}

              <GradientDivider />

              <SuggestionActionButtons
                acceptLabel={acceptLabel}
                onAccept={handleAccept}
                onDecline={handleDecline}
                centered
              />
            </Paper>
          ) : (
            <Paper
              onMouseEnter={handlePopperMouseEnter}
              onMouseLeave={handlePopperMouseLeave}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              elevation={4}
              sx={compactPaperSx}
            >
              <Box component="img" src="/images/scalar_ai.png" alt="Scalar AI" sx={compactIconSx} />

              <Box sx={compactContentSx}>
                <SuggestionActionButtons
                  acceptLabel="Accept"
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                />

                <Typography variant="body2" sx={whySuggestionTextSx}>
                  Why this{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    onClick={handleExpand}
                    sx={suggestionLinkSx}
                  >
                    suggestion
                  </Typography>
                  ?
                </Typography>
              </Box>
            </Paper>
          )}
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default AISuggestionPopover;
